import { HttpClient, httpResource } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Signal } from '@angular/core';
import { Observable } from 'rxjs';
import { IPaginatedResponse } from '@dss-libs/shared/type/common-types';
import { API_URL } from '@dss-libs/shared/utility/global-injection-tokens';
import {
  IOrderDetailsModel,
  IOrderActivityModel,
  IOrderResponse,
  IOrderStatistics,
  IOrderRecipientStatusDetails,
  IOrderSearchModel,
  IProviderMethod,
} from '@dss-libs/portal/type/portal-types';
import { ORDER_URLS } from '../../urls/order.urls';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  readonly #http = inject(HttpClient);
  readonly #apiUrl = inject(API_URL);

  ordersListingResource(params: Signal<IOrderSearchModel>) {
    return httpResource<IPaginatedResponse<IOrderResponse>>(() => {
      return {
        url: `${this.#apiUrl}${ORDER_URLS.GET_ORDER}`,
        params: { ...params() },
      };
    });
  }

  createOrder(createModel: FormData): Observable<void> {
    const apiUrl = `${this.#apiUrl}${ORDER_URLS.CREATE_ORDER}`;
    return this.#http.post<void>(apiUrl, createModel);
  }

  getOrderDetailsResource(orderId: Signal<string>) {
    return httpResource<IOrderDetailsModel>(() => `${this.#apiUrl}${ORDER_URLS.GET_ORDER_BY_ID(orderId())}`);
  }

  getOrderActivitiesResource(orderId: Signal<string>) {
    return httpResource<IOrderActivityModel[]>(() => `${this.#apiUrl}${ORDER_URLS.GET_ORDER_ACTIVITY(orderId())}`, {
      defaultValue: [],
    });
  }

  orderStatisticsResource(statuses: Signal<string[]>) {
    return httpResource<IOrderStatistics>(() => ({
      url: `${this.#apiUrl}${ORDER_URLS.ORDERS_STATISTICS}`,
      params: { statuses: statuses() },
    }));
  }

  cancelOrder(orderId: string): Observable<void> {
    const apiUrl = `${this.#apiUrl}${ORDER_URLS.CANCEL_ORDER(orderId)}`;
    return this.#http.post<void>(apiUrl, {});
  }

  signingMethodsResource() {
    return httpResource<IProviderMethod[]>(() => `${this.#apiUrl}${ORDER_URLS.SIGNING_METHODS}`);
  }

  sendReminders(orderId: string, recipientIds: string[]): Observable<void> {
    const apiUrl = `${this.#apiUrl}${ORDER_URLS.SEND_REMINDER(orderId)}`;
    return this.#http.post<void>(apiUrl, { recipientIds });
  }

  orderStatusDetailsResource(orderId: Signal<string | null>) {
    return httpResource<IOrderRecipientStatusDetails[]>(
      () => {
        const id = orderId();
        return id ? `${this.#apiUrl}${ORDER_URLS.ORDER_STATUS_DETAILS(id)}` : undefined;
      },
      { defaultValue: [] }
    );
  }
}
